<template>
	<v-container fluid class="viagem-page pa-8 pt-12">
		<div class="viagem-header">
			<div>
				<h1 class="viagem-title">Viagens</h1>
				<p class="viagem-subtitle">
					Acompanhe as viagens em andamento, controle status e tenha visao rapida da operacao.
				</p>
			</div>
			<div class="viagem-header__actions">
				<v-btn class="viagem-secondary-btn" outlined @click="abrirDialogContratantes">
					<v-icon left size="18">mdi-domain</v-icon>
					Contratantes
				</v-btn>
				<v-btn class="viagem-new-btn" depressed @click="novaViagem">
					<v-icon left size="18">mdi-plus</v-icon>
					Nova Viagem
				</v-btn>
			</div>
		</div>

		<v-row class="mt-6" dense>
			<v-col cols="12" md="4">
				<v-card class="viagem-stat" outlined>
					<div class="viagem-stat__content">
						<div>
							<div class="viagem-stat__label">Total de viagens</div>
							<div class="viagem-stat__value">{{ totalViagens }}</div>
						</div>
						<div class="viagem-stat__icon viagem-stat__icon--primary">
							<v-icon color="primary" size="20">mdi-map-marker-path</v-icon>
						</div>
					</div>
				</v-card>
			</v-col>

			<v-col cols="12" md="4">
				<v-card class="viagem-stat" outlined>
					<div class="viagem-stat__content">
						<div>
							<div class="viagem-stat__label">Em andamento</div>
							<div class="viagem-stat__value" :style="{ color: accentColor }">{{ viagensEmAndamento }}</div>
						</div>
						<div class="viagem-stat__icon viagem-stat__icon--accent">
							<v-icon :color="accentColor" size="20">mdi-truck-fast-outline</v-icon>
						</div>
					</div>
				</v-card>
			</v-col>

			<v-col cols="12" md="4">
				<v-card class="viagem-stat" outlined>
					<div class="viagem-stat__content">
						<div>
							<div class="viagem-stat__label">Finalizadas</div>
							<div class="viagem-stat__value is-done">{{ viagensFinalizadas }}</div>
						</div>
						<div class="viagem-stat__icon viagem-stat__icon--done">
							<v-icon color="#2563eb" size="20">mdi-check-decagram-outline</v-icon>
						</div>
					</div>
				</v-card>
			</v-col>
		</v-row>

		<v-card class="viagem-table-card mt-6" outlined>
			<div class="viagem-table-card__toolbar">
				<v-menu offset-y left :close-on-content-click="false">
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							icon
							class="viagem-icon-btn"
							v-bind="attrs"
							v-on="on"
							title="Colunas"
							aria-label="Colunas"
						>
							<v-icon size="18">mdi-cog-outline</v-icon>
						</v-btn>
					</template>

					<v-card class="viagem-colunas-card" outlined>
						<v-card-title class="viagem-colunas-card__title">
							<span>Colunas visiveis</span>
							<v-spacer />
							<v-btn text small color="#10b981" @click="restaurarColunasPadrao">
								Padrao
							</v-btn>
						</v-card-title>
						<v-divider />
						<v-card-text class="pt-4 pb-3">
							<div class="viagem-colunas-card__hint">
								Escolha quais colunas da viagem devem aparecer na tabela.
							</div>
							<v-checkbox
								v-for="column in columnOptions"
								:key="column.value"
								:input-value="isColumnVisible(column.value)"
								:label="column.text"
								hide-details
								dense
								class="viagem-colunas-card__checkbox mt-0"
								@change="toggleVisibleColumn(column.value)"
							/>
						</v-card-text>
					</v-card>
				</v-menu>

				<v-badge
					:value="hasActiveFilters"
					color="red"
					dot
					overlap
				>
					<v-btn
						icon
						class="viagem-icon-btn"
						title="Filtros"
						aria-label="Filtros"
						@click="abrirFiltros"
					>
						<v-icon size="18">mdi-filter-outline</v-icon>
					</v-btn>
				</v-badge>
			</div>
			<v-data-table
				class="viagem-table"
				:headers="visibleHeaders"
				:items="filteredTableItems"
				:loading="loading"
				loading-text="Carregando viagens..."
				no-data-text="Nenhuma viagem cadastrada"
				no-results-text="Nenhum resultado encontrado. Por favor, tente outra pesquisa."
				:items-per-page="8"
				:footer-props="{
					itemsPerPageText: 'Itens por Pagina',
					itemsPerPageOptions: [10, 15, 20],
					pageText: '{0}-{1} de {2}'
				}"
			>
				<template v-slot:[`item.rota`]='{ item }'>
					<div class="viagem-rota">
						<span class="viagem-rota__origem">{{ item.origem }}</span>
						<v-icon size="14" color="#94a3b8" class="mx-1">mdi-arrow-right</v-icon>
						<span>{{ item.destino }}</span>
					</div>
				</template>

				<template v-slot:[`item.veiculo`]='{ item }'>
					<div class="viagem-veiculo">
						<v-icon size="16" color="#10b981" class="mr-1">mdi-truck-outline</v-icon>
						<span>{{ item.veiculo }}</span>
					</div>
				</template>

				<template v-slot:[`item.status`]='{ item }'>
					<span class="viagem-status" :class="statusClass(item.status)">
						<v-icon size="14" class="mr-1">{{ statusIcon(item.status) }}</v-icon>
						{{ item.status }}
					</span>
				</template>

				<template v-slot:[`item.despesa`]='{ item }'>
					<div class="viagem-despesa-cell">
						<span>{{ item.despesa }}</span>
						<v-btn icon x-small class="viagem-despesa-cell__action" @click.stop="abrirDialogDespesas(item)">
							<v-icon size="16" :color="accentColor">mdi-pencil-outline</v-icon>
						</v-btn>
					</div>
				</template>

				<template v-slot:[`item.acoes`]='{ item }'>
					<v-menu offset-y left>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								icon
								small
								class="viagem-actions"
								v-bind="attrs"
								v-on="on"
							>
								<v-icon :color="accentColor">mdi-dots-vertical</v-icon>
							</v-btn>
						</template>

						<v-list dense class="viagem-actions__menu">
							<v-list-item @click="editarViagem(item)">
								<v-list-item-icon>
									<v-icon :color="accentColor" size="18">mdi-pencil-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Editar</v-list-item-title>
							</v-list-item>

							<v-divider />

							<v-list-item @click="abrirDialogStatus(item)">
								<v-list-item-icon>
									<v-icon :color="accentColor" size="18">mdi-ballot-recount</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Editar status</v-list-item-title>
							</v-list-item>

							<v-divider />

							<v-list-item @click="verDetalhes(item)">
								<v-list-item-icon>
									<v-icon :color="accentColor" size="18">mdi-map-search-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Detalhes</v-list-item-title>
							</v-list-item>

							<v-divider />

							<v-list-item @click="abrirDialogExclusao(item)">
								<v-list-item-icon>
									<v-icon color="error" size="18">mdi-delete-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Excluir</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</template>
			</v-data-table>
		</v-card>

		<CadastroViagemDialog
			v-model="cadastroDialogOpen"
			@saved="handleCadastroSalvo"
		/>

		<EditarViagemDialog
			:value="editarDialogOpen"
			:viagem="viagemEmEdicao"
			@input="handleEdicaoDialog"
			@saved="handleEdicaoSalva"
		/>

		<v-dialog v-model="contratantesDialogOpen" max-width="860" persistent scrollable>
			<v-card class="viagem-dialog" outlined>
				<v-card-title class="viagem-dialog__title">
					<span>Contratantes</span>
					<v-spacer />
					<v-btn class="viagem-new-btn mr-3" small depressed @click="abrirDialogCadastroContratante">
						<v-icon left size="16">mdi-plus</v-icon>
						Cadastrar
					</v-btn>
					<v-btn icon @click="contratantesDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-5">
					<v-data-table
						class="viagem-table"
						:headers="contratantesHeaders"
						:items="contratantes"
						:loading="loadingContratantes"
						loading-text="Carregando contratantes..."
						no-data-text="Nenhum contratante cadastrado"
						:disable-pagination="contratantes.length <= 10"
						:hide-default-footer="contratantes.length <= 10"
					>
						<template v-slot:[`item.cnpj`]='{ item }'>
							<span>{{ formatCnpj(item.cnpj) || '-' }}</span>
						</template>

						<template v-slot:[`item.acoes`]='{ item }'>
							<div class="contratante-table-actions">
								<v-btn icon small @click="abrirDialogEditarContratante(item)">
									<v-icon color="#10b981" size="18">mdi-pencil-outline</v-icon>
								</v-btn>
								<v-btn icon small @click="abrirDialogExcluirContratante(item)">
									<v-icon color="#ef4444" size="18">mdi-delete-outline</v-icon>
								</v-btn>
							</div>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-dialog>

		<v-dialog v-model="contratanteFormDialogOpen" max-width="420" persistent>
			<v-card class="viagem-dialog" outlined>
				<v-card-title class="viagem-dialog__title">
					<span>{{ contratanteDialogTitle }}</span>
					<v-spacer />
					<v-btn icon :disabled="savingContratante" @click="contratanteFormDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<v-form ref="contratanteFormRef" v-model="contratanteFormValid" lazy-validation>
						<v-row dense>
							<v-col cols="12">
								<v-text-field
									v-model.trim="contratanteForm.nome"
									label="Nome"
									outlined
									dense
									:rules="[contratanteRules.required]"
								/>
							</v-col>
							<v-col cols="12">
								<v-text-field
									v-model="contratanteForm.cnpj"
									label="CNPJ"
									outlined
									dense
									inputmode="numeric"
									@input="onContratanteCnpjInput"
								/>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="savingContratante" @click="contratanteFormDialogOpen = false">Cancelar</v-btn>
					<v-btn class="viagem-new-btn" depressed :loading="savingContratante" :disabled="savingContratante" @click="salvarContratante">
						{{ contratanteDialogSaveLabel }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="contratanteDeleteDialogOpen" max-width="420" persistent>
			<v-card class="viagem-dialog" outlined>
				<v-card-title class="viagem-dialog__title">
					<span>Excluir Contratante</span>
					<v-spacer />
					<v-btn icon :disabled="deletingContratante" @click="contratanteDeleteDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="viagem-confirmacao__texto">
						Tem certeza que deseja excluir o contratante <strong>{{ contratanteSelecionado.nome || '-' }}</strong>?
					</div>
					<div class="viagem-confirmacao__aviso">
						Se ele estiver vinculado a viagens existentes, o campo da viagem ficara vazio.
					</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="deletingContratante" @click="contratanteDeleteDialogOpen = false">Cancelar</v-btn>
					<v-btn class="viagem-delete-btn" depressed :loading="deletingContratante" :disabled="deletingContratante" @click="confirmarExclusaoContratante">
						Excluir
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="statusDialogOpen" max-width="320" persistent>
			<v-card>
				<v-card-title class="viagem-dialog__title">
					<span>Editar status</span>
					<v-spacer />
					<v-btn icon :disabled="statusSaving" @click="statusDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<v-row dense>
						<v-col cols="12">
							<v-select
								v-model="viagemStatusEdicao.status"
								:items="statusViagem"
								label="Status"
								outlined
								dense
							/>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="statusSaving" @click="statusDialogOpen = false">Cancelar</v-btn>
					<v-btn class="viagem-new-btn" depressed :loading="statusSaving" :disabled="statusSaving" @click="salvarStatusViagem">
						Salvar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="exclusaoDialogOpen" max-width="420" persistent>
			<v-card>
				<v-card-title class="viagem-dialog__title">
					<span>Excluir viagem</span>
					<v-spacer />
					<v-btn icon :disabled="exclusaoSaving" @click="exclusaoDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="viagem-confirmacao__texto">
						Tem certeza que deseja excluir a viagem <strong>{{ viagemExclusao.codigo || '-' }}</strong>?
					</div>
					<div class="viagem-confirmacao__aviso">
						As paradas vinculadas tambem serao removidas.
					</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="exclusaoSaving" @click="exclusaoDialogOpen = false">Cancelar</v-btn>
					<v-btn class="viagem-delete-btn" depressed :loading="exclusaoSaving" :disabled="exclusaoSaving" @click="confirmarExclusaoViagem">
						Excluir
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="detalhesDialogOpen" max-width="760" scrollable>
			<v-card class="viagem-dialog" outlined>
				<v-card-title class="viagem-dialog__title">
					<span>Detalhes da Viagem</span>
					<v-spacer />
					<v-btn icon @click="fecharDetalhes">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider />

				<v-card-text class="pt-6 pb-4">
					<div v-if="detalhesLoading" class="viagem-detalhes__loading">
						<v-progress-circular indeterminate color="#10b981" size="28" width="3" />
					</div>

					<v-container v-else class="pa-0" fluid>
						<v-row dense>
							<v-col cols="12" md="6"><strong>Codigo:</strong> {{ detalhesAtual.codigo_carga || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Status:</strong> {{ detalhesAtual.status || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Contratante:</strong> {{ detalhesAtual.contratante_nome || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Motorista:</strong> {{ detalhesAtual.motorista_nome || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Veiculo:</strong> {{ detalhesAtual.veiculo_placa || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Saida:</strong> {{ formatDate(detalhesAtual.data_saida) }}</v-col>
							<v-col cols="12" md="6"><strong>Previsao:</strong> {{ formatDate(detalhesAtual.previsao_chegada) }}</v-col>
							<v-col cols="12" md="6"><strong>Frete Bruto:</strong> {{ formatCurrency(resolveFreteBruto(detalhesAtual)) }}</v-col>
							<v-col cols="12" md="6"><strong>Imposto (%):</strong> {{ formatPercent(resolveImpostoPercentual(detalhesAtual)) }}</v-col>
							<v-col cols="12" md="6"><strong>Frete Liquido:</strong> {{ formatCurrency(resolveFreteLiquido(detalhesAtual)) }}</v-col>
							<v-col cols="12" md="6"><strong>Despesas:</strong> {{ formatCurrency(detalhesAtual.despesa_total) }}</v-col>
							<v-col cols="12" md="6"><strong>Peso:</strong> {{ detalhesAtual.peso || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>KM Inicial:</strong> {{ formatKm(detalhesAtual.km_inicial) }}</v-col>
							<v-col cols="12" md="6"><strong>Tipo de operacao:</strong> {{ detalhesAtual.tipo_operacao || '-' }}</v-col>
							<v-col cols="12"><strong>Observacoes:</strong> {{ detalhesAtual.observacoes || '-' }}</v-col>
						</v-row>

						<div class="viagem-section-title mt-6">Paradas</div>
						<v-divider class="mb-4" />

						<div v-if="!paradasDetalhes.length" class="viagem-detalhes__empty">
							Nenhuma parada cadastrada para esta viagem.
						</div>

						<v-timeline
							v-else
							dense
							align-top
							class="viagem-paradas-timeline"
						>
							<v-timeline-item
								v-for="parada in paradasDetalhes"
								:key="parada.id || parada.ordem"
								:color="getParadaTimelineColor(parada.tipo)"
								:icon="getParadaTimelineIcon(parada.tipo)"
								fill-dot
								small
							>
								<div class="parada-box mb-3">
									<div class="parada-box__header">
										<div class="parada-box__identity">
											<span class="parada-box__eyebrow">Parada {{ parada.ordem || '-' }}</span>
											<span class="parada-box__subtitle">Ponto da rota da viagem</span>
										</div>
										<span class="parada-box__tipo">{{ parada.tipo || '-' }}</span>
									</div>
									<div class="parada-box__details">
										<div class="parada-box__detail-card">
											<div class="parada-box__detail-header">
												<v-icon size="16" color="#10b981">mdi-map-marker-outline</v-icon>
												<span class="parada-box__detail-label">Cidade / UF</span>
											</div>
											<strong>{{ parada.cidade_uf || '-' }}</strong>
										</div>
										<div class="parada-box__detail-card">
											<div class="parada-box__detail-header">
												<v-icon size="16" color="#2563eb">mdi-domain</v-icon>
												<span class="parada-box__detail-label">Cliente</span>
											</div>
											<strong>{{ parada.cliente || 'Nao informado' }}</strong>
										</div>
									</div>
									<div class="parada-box__note">
										<span class="parada-box__detail-label">Observacoes</span>
										<p>{{ parada.observacoes || 'Sem observacoes para esta parada.' }}</p>
									</div>
								</div>
							</v-timeline-item>
						</v-timeline>
					</v-container>
				</v-card-text>

				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text @click="fecharDetalhes">Fechar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="filterDialogOpen" max-width="520" persistent>
			<v-card class="viagem-dialog" outlined>
				<v-card-title class="viagem-dialog__title">
					<span>Filtrar viagens</span>
					<v-spacer />
					<v-btn icon @click="filterDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-0">
					<v-row dense>
						<v-col cols="12" md="6">
							<v-select
								v-model="filterForm.status"
								:items="statusFilterOptions"
								label="Status"
								outlined
								dense
								clearable
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-autocomplete
								v-model="filterForm.motorista"
								:items="motoristaFilterOptions"
								label="Motorista"
								outlined
								dense
								clearable
								no-data-text="Nenhum motorista encontrado"
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-autocomplete
								v-model="filterForm.veiculo"
								:items="veiculoFilterOptions"
								label="Veículo (placa)"
								outlined
								dense
								clearable
								no-data-text="Nenhum veículo encontrado"
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-autocomplete
								v-model="filterForm.contratante"
								:items="contratanteFilterOptions"
								label="Contratante"
								outlined
								dense
								clearable
								no-data-text="Nenhum contratante encontrado"
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								v-model="filterForm.data_saida_de"
								label="Saída de"
								type="date"
								outlined
								dense
							/>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								v-model="filterForm.data_saida_ate"
								label="Saída até"
								type="date"
								outlined
								dense
							/>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="px-6 pb-6">
					<v-btn text @click="limparFiltros">Limpar</v-btn>
					<v-spacer />
					<v-btn text @click="filterDialogOpen = false">Cancelar</v-btn>
					<v-btn class="viagem-new-btn" depressed @click="aplicarFiltros">
						Filtrar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<ViagemDespesasDialog
			:value="despesaDialogOpen"
			:viagem="viagemDespesa"
			@input="handleDespesaDialog"
			@saved="handleDespesaSalva"
		/>
	</v-container>
</template>

<script>
import contratantesServices from './contratantesServices';
import viagensServices from './viagensServices';
import CadastroViagemDialog from './cadastroViagem.vue';
import EditarViagemDialog from './editarViagem.vue';
import ViagemDespesasDialog from './viagemDespesasDialog.vue';

const VIAGENS_VISIBLE_COLUMNS_STORAGE_KEY = 'machado_web_transportadoras.viagens.visible_columns.v3';
const AVAILABLE_HEADERS = [
	{ text: 'Codigo', value: 'codigo', sortable: false },
	{ text: 'Contratante', value: 'contratante', sortable: false },
	{ text: 'Rota', value: 'rota', sortable: false },
	{ text: 'Motorista', value: 'motorista', sortable: false },
	{ text: 'Veiculo', value: 'veiculo', sortable: false },
	{ text: 'Saida', value: 'saida', sortable: false },
	{ text: 'Previsao', value: 'previsao', sortable: false },
	{ text: 'Frete Bruto', value: 'frete', sortable: false },
	{ text: 'Imposto (%)', value: 'imposto', sortable: false },
	{ text: 'Frete Liquido', value: 'frete_liquido', sortable: false },
	{ text: 'Peso', value: 'peso', sortable: false },
	{ text: 'KM Inicial', value: 'km_inicial', sortable: false },
	{ text: 'Operacao', value: 'tipoOperacao', sortable: false },
	{ text: 'Despesa', value: 'despesa', sortable: false },
	{ text: 'Status', value: 'status', sortable: false },
	{ text: 'Acoes', value: 'acoes', sortable: false, align: 'center' },
];
const DEFAULT_VISIBLE_COLUMNS = ['codigo', 'rota', 'motorista', 'veiculo', 'saida', 'km_inicial', 'frete', 'imposto', 'frete_liquido', 'status'];

export default {
	name: 'ListagemViagens',
	components: {
		CadastroViagemDialog,
		EditarViagemDialog,
		ViagemDespesasDialog,
	},

	data() {
		return {
			accentColor: '#10b981',
			loading: false,
			loadingContratantes: false,
			savingContratante: false,
			deletingContratante: false,
			cadastroDialogOpen: false,
			editarDialogOpen: false,
			contratantesDialogOpen: false,
			contratanteFormDialogOpen: false,
			contratanteDeleteDialogOpen: false,
			contratanteFormValid: false,
			statusDialogOpen: false,
			statusSaving: false,
			exclusaoDialogOpen: false,
			exclusaoSaving: false,
			detalhesDialogOpen: false,
			detalhesLoading: false,
			despesaDialogOpen: false,
			viagemEmEdicao: null,
			viagemDetalhes: null,
			viagemDespesa: {},
			viagemExclusao: {},
			statusViagem: [
				'Pendente',
				'Em carregamento',
				'Em Transito',
				'Aguardando Descarga',
				'Finalizada',
				'Cancelada',
			],
			viagemStatusEdicao: {
				id: null,
				status: null,
			},
			contratantes: [],
			contratanteSelecionado: {},
			contratanteForm: {
				id: null,
				nome: '',
				cnpj: '',
			},
			contratanteRules: {
				required: (v) => Boolean(v || v === 0) || 'Campo obrigatorio',
			},
			viagens: [],
			visibleColumnValues: [...DEFAULT_VISIBLE_COLUMNS],
			filterDialogOpen: false,
			filterForm: {
				status: null,
				motorista: '',
				veiculo: '',
				contratante: '',
				data_saida_de: '',
				data_saida_ate: '',
			},
			activeFilters: {
				status: null,
				motorista: '',
				veiculo: '',
				contratante: '',
				data_saida_de: '',
				data_saida_ate: '',
			},
			statusFilterOptions: ['Pendente', 'Em andamento', 'Carregando', 'Ag. Descarga', 'Finalizada', 'Cancelada'],
			contratantesHeaders: [
				{ text: 'Nome', value: 'nome', sortable: false },
				{ text: 'CNPJ', value: 'cnpj', sortable: false },
				{ text: 'Acoes', value: 'acoes', sortable: false, align: 'center' },
			],
		};
	},

	computed: {
		isEditingContratante() {
			return Boolean(this.contratanteForm && this.contratanteForm.id);
		},

		contratanteDialogTitle() {
			return this.isEditingContratante ? 'Editar Contratante' : 'Novo Contratante';
		},

		contratanteDialogSaveLabel() {
			return this.isEditingContratante ? 'Salvar alteracoes' : 'Salvar contratante';
		},

		columnOptions() {
			return AVAILABLE_HEADERS.filter((header) => header.value !== 'acoes' && header.value !== 'despesa');
		},

		visibleHeaders() {
			return AVAILABLE_HEADERS.filter(
				(header) => header.value === 'acoes' || header.value === 'despesa' || this.visibleColumnValues.includes(header.value),
			);
		},

		tableItems() {
			if (!Array.isArray(this.viagens)) {
				return [];
			}

			return this.viagens.map((viagem) => {
				const rota = this.extrairRota(viagem);

				return {
					id: viagem.id,
					codigo: viagem.codigo_carga || `#${viagem.id || '-'}`,
					contratante: viagem.contratante_nome || '-',
					origem: rota.origem,
					destino: rota.destino,
					motorista: this.formatMotoristaNome(viagem.motorista_nome),
					veiculo: viagem.veiculo_placa || '-',
					saida: this.formatDate(viagem.data_saida),
					previsao: this.formatDate(viagem.previsao_chegada),
					frete: this.formatCurrency(this.resolveFreteBruto(viagem)),
					imposto: this.formatPercent(this.resolveImpostoPercentual(viagem)),
					frete_liquido: this.formatCurrency(this.resolveFreteLiquido(viagem)),
					peso: this.formatPeso(viagem.peso),
					km_inicial: this.formatKm(viagem.km_inicial),
					tipoOperacao: viagem.tipo_operacao || '-',
					despesa: this.formatCurrency(this.resolveDespesaTotal(viagem)),
					status: this.normalizeStatus(viagem.status),
					_data_saida_raw: viagem.data_saida ? String(viagem.data_saida).slice(0, 10) : '',
				};
			});
		},

		totalViagens() {
			return this.tableItems.length;
		},

		viagensEmAndamento() {
			return this.tableItems.filter(
				(item) => ['Em andamento', 'Carregando', 'Ag. Descarga'].includes(item.status),
			).length;
		},

		viagensFinalizadas() {
			return this.tableItems.filter((item) => item.status === 'Finalizada').length;
		},

		detalhesAtual() {
			return this.viagemDetalhes || {};
		},

		paradasDetalhes() {
			if (!this.viagemDetalhes || !Array.isArray(this.viagemDetalhes.paradas)) {
				return [];
			}

			return this.viagemDetalhes.paradas;
		},

		motoristaFilterOptions() {
			const names = new Set();
			this.tableItems.forEach((item) => {
				if (item.motorista && item.motorista !== '-') names.add(item.motorista);
			});
			return [...names].sort((a, b) => a.localeCompare(b, 'pt-BR'));
		},

		veiculoFilterOptions() {
			const plates = new Set();
			this.tableItems.forEach((item) => {
				if (item.veiculo && item.veiculo !== '-') plates.add(item.veiculo);
			});
			return [...plates].sort();
		},

		contratanteFilterOptions() {
			return this.contratantes
				.map((c) => c.nome)
				.filter(Boolean)
				.sort((a, b) => a.localeCompare(b, 'pt-BR'));
		},

		hasActiveFilters() {
			const f = this.activeFilters;
			return Boolean(f.status || f.motorista || f.veiculo || f.contratante || f.data_saida_de || f.data_saida_ate);
		},

		filteredTableItems() {
			const f = this.activeFilters;
			return this.tableItems.filter((item) => {
				if (f.status && item.status !== f.status) return false;
				if (f.motorista && item.motorista !== f.motorista) return false;
				if (f.veiculo && item.veiculo !== f.veiculo) return false;
				if (f.contratante && item.contratante !== f.contratante) return false;
				if (f.data_saida_de && (!item._data_saida_raw || item._data_saida_raw < f.data_saida_de)) return false;
				if (f.data_saida_ate && (!item._data_saida_raw || item._data_saida_raw > f.data_saida_ate)) return false;
				return true;
			});
		},
	},

	methods: {
		normalizeVisibleColumns(values) {
			const allowedValues = AVAILABLE_HEADERS
				.filter((header) => header.value !== 'acoes' && header.value !== 'despesa')
				.map((header) => header.value);
			const selectedValues = new Set(Array.isArray(values) ? values : []);
			const normalizedValues = allowedValues.filter((value) => selectedValues.has(value));

			return normalizedValues.length ? normalizedValues : [...DEFAULT_VISIBLE_COLUMNS];
		},

		loadVisibleColumnsPreference() {
			try {
				const storedColumns = localStorage.getItem(VIAGENS_VISIBLE_COLUMNS_STORAGE_KEY);
				if (!storedColumns) {
					return;
				}

				this.visibleColumnValues = this.normalizeVisibleColumns(JSON.parse(storedColumns));
			} catch (error) {
				this.visibleColumnValues = [...DEFAULT_VISIBLE_COLUMNS];
			}
		},

		persistVisibleColumnsPreference() {
			try {
				localStorage.setItem(
					VIAGENS_VISIBLE_COLUMNS_STORAGE_KEY,
					JSON.stringify(this.visibleColumnValues),
				);
			} catch (error) {
				// Ignora falhas de armazenamento local sem interromper a tela.
			}
		},

		isColumnVisible(columnValue) {
			return this.visibleColumnValues.includes(columnValue);
		},

		toggleVisibleColumn(columnValue) {
			const isVisible = this.isColumnVisible(columnValue);

			if (isVisible && this.visibleColumnValues.length === 1) {
				this.$toasted?.show('Mantenha ao menos uma coluna visivel na tabela.');
				return;
			}

			const nextValues = isVisible
				? this.visibleColumnValues.filter((value) => value !== columnValue)
				: [...this.visibleColumnValues, columnValue];

			this.visibleColumnValues = this.normalizeVisibleColumns(nextValues);
			this.persistVisibleColumnsPreference();
		},

		restaurarColunasPadrao() {
			this.visibleColumnValues = [...DEFAULT_VISIBLE_COLUMNS];
			this.persistVisibleColumnsPreference();
			this.$toasted?.show('Colunas restauradas para o padrao.');
		},

		abrirFiltros() {
			this.filterForm = { ...this.activeFilters };
			this.filterDialogOpen = true;
		},

		aplicarFiltros() {
			this.activeFilters = { ...this.filterForm };
			this.filterDialogOpen = false;
		},

		limparFiltros() {
			const empty = { status: null, motorista: '', veiculo: '', contratante: '', data_saida_de: '', data_saida_ate: '' };
			this.filterForm = { ...empty };
			this.activeFilters = { ...empty };
			this.filterDialogOpen = false;
		},

		resolveDespesaTotal(viagem) {
			const camposDiretos = [
				viagem?.despesa_total,
				viagem?.despesas_total,
				viagem?.total_despesas,
				viagem?.valor_despesas,
			];

			for (const valor of camposDiretos) {
				const numericValue = Number(valor);
				if (Number.isFinite(numericValue)) {
					return numericValue;
				}
			}

			if (Array.isArray(viagem?.despesas)) {
				return viagem.despesas.reduce((total, despesa) => {
					const numericValue = Number(despesa?.valor);
					return Number.isFinite(numericValue) ? total + numericValue : total;
				}, 0);
			}

			return 0;
		},

		formatCnpj(value) {
			const digitsOnly = String(value || '').replace(/\D/g, '').slice(0, 14);
			if (!digitsOnly) {
				return '';
			}

			return digitsOnly
				.replace(/^(\d{2})(\d)/, '$1.$2')
				.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
				.replace(/\.(\d{3})(\d)/, '.$1/$2')
				.replace(/(\/\d{4})(\d)/, '$1-$2');
		},

		parseCnpj(value) {
			const digitsOnly = String(value || '').replace(/\D/g, '').slice(0, 14);
			return digitsOnly || null;
		},

		buildContratanteLabel(item) {
			if (!item) {
				return '-';
			}

			return item.cnpj ? `${item.nome} - ${this.formatCnpj(item.cnpj)}` : item.nome;
		},

		async fetchContratantes() {
			this.loadingContratantes = true;
			try {
				const response = await contratantesServices.listar();
				const data = Array.isArray(response.data) ? response.data : [];
				this.contratantes = data.map((item) => ({
					id: item.id,
					nome: item.nome || '-',
					cnpj: item.cnpj || null,
					label: this.buildContratanteLabel(item),
				}));
			} catch (error) {
				this.contratantes = [];
			}
			this.loadingContratantes = false;
		},

		extrairRota(viagem) {
			const paradas = Array.isArray(viagem?.paradas) ? viagem.paradas : [];
			const paradasComCidade = paradas.filter((parada) => parada?.cidade_uf);

			if (paradasComCidade.length === 0) {
				const origemFallback = viagem?.origem || '-';
				return {
					origem: origemFallback,
					destino: viagem?.destino || origemFallback,
				};
			}

			const origemParada = paradasComCidade.find((parada) => this.isParadaCarrega(parada)) || paradasComCidade[0];
			const entregas = paradasComCidade.filter((parada) => this.isParadaEntrega(parada));
			const paradasSemRetorno = paradasComCidade.filter((parada) => !this.isParadaRetorno(parada));
			const destinoParada = entregas[entregas.length - 1]
				|| paradasSemRetorno[paradasSemRetorno.length - 1]
				|| paradasComCidade[paradasComCidade.length - 1]
				|| origemParada;
			const origem = origemParada?.cidade_uf || viagem?.origem || '-';
			const destino = destinoParada?.cidade_uf || viagem?.destino || origem || '-';

			return { origem, destino };
		},

		normalizeTipoParada(tipo) {
			return String(tipo || '')
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim();
		},

		isParadaCarrega(parada) {
			const tipo = this.normalizeTipoParada(parada?.tipo);
			return tipo.includes('carrega') || tipo.includes('coleta') || tipo.includes('origem');
		},

		isParadaEntrega(parada) {
			const tipo = this.normalizeTipoParada(parada?.tipo);
			return tipo.includes('entrega') || tipo.includes('descarga') || tipo.includes('destino');
		},

		isParadaRetorno(parada) {
			const tipo = this.normalizeTipoParada(parada?.tipo);
			return tipo.includes('retorna') || tipo.includes('retorno');
		},

		async fetchViagens() {
			this.loading = true;
			try {
				const response = await viagensServices.listar();
				this.viagens = Array.isArray(response.data) ? response.data : [];
			} catch (error) {
				this.viagens = [];
			}
			this.loading = false;
		},

		formatDate(value) {
			if (!value) {
				return '-';
			}

			const date = new Date(value);
			if (Number.isNaN(date.getTime())) {
				return String(value);
			}

			return date.toLocaleDateString('pt-BR');
		},

		toNumber(value, fallback = 0) {
			if (value === null || value === undefined || value === '') {
				return fallback;
			}

			const numericValue = Number(value);
			return Number.isFinite(numericValue) ? numericValue : fallback;
		},

		resolveFreteBruto(viagem) {
			return this.toNumber(viagem?.frete_bruto ?? viagem?.valor_frete, 0);
		},

		resolveImpostoPercentual(viagem) {
			return this.toNumber(viagem?.imposto_percentual ?? viagem?.imposto, 0);
		},

		resolveFreteLiquido(viagem) {
			const freteLiquido = Number(viagem?.frete_liquido);
			if (Number.isFinite(freteLiquido)) {
				return freteLiquido;
			}

			const freteBruto = this.resolveFreteBruto(viagem);
			const impostoPercentual = this.resolveImpostoPercentual(viagem);

			return freteBruto - (freteBruto * impostoPercentual / 100);
		},

		formatCurrency(value) {
			if (value === null || value === undefined || value === '') {
				return '-';
			}

			const numericValue = Number(value);
			if (!Number.isFinite(numericValue)) {
				return String(value);
			}

			return numericValue.toLocaleString('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			});
		},

		formatPercent(value) {
			if (value === null || value === undefined || value === '') {
				return '-';
			}

			const numericValue = Number(value);
			if (!Number.isFinite(numericValue)) {
				return String(value);
			}

			return `${numericValue.toLocaleString('pt-BR', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			})}%`;
		},

		formatPeso(value) {
			if (value === null || value === undefined || value === '') {
				return '-';
			}

			const numericValue = Number(value);
			if (!Number.isFinite(numericValue)) {
				return String(value);
			}

			return `${numericValue.toLocaleString('pt-BR')} kg`;
		},

		formatKm(value) {
			if (value === null || value === undefined || value === '') {
				return '-';
			}

			const numericValue = Number(value);
			if (!Number.isFinite(numericValue)) {
				return String(value);
			}

			return `${numericValue.toLocaleString('pt-BR')} km`;
		},

		formatMotoristaNome(nome) {
			const partes = String(nome || '')
				.trim()
				.split(/\s+/)
				.filter(Boolean);

			if (partes.length === 0) {
				return '-';
			}

			if (partes.length === 1) {
				return partes[0];
			}

			const [primeiroNome, ...restante] = partes;
			const abreviacoes = restante
				.map((parte) => `${parte.charAt(0).toUpperCase()}.`)
				.join(' ');

			return `${primeiroNome} ${abreviacoes}`;
		},

		normalizeStatus(status) {
			const normalized = String(status || '').toLowerCase().trim();
			if (normalized.includes('carregamento')) {
				return 'Carregando';
			}
			if (normalized.includes('aguardando descarga') || normalized.includes('descarga')) {
				return 'Ag. Descarga';
			}
			if (normalized.includes('andamento') || normalized.includes('transito')) {
				return 'Em andamento';
			}
			if (normalized.includes('final') || normalized.includes('entreg')) {
				return 'Finalizada';
			}
			if (normalized.includes('cancel')) {
				return 'Cancelada';
			}
			return status || 'Pendente';
		},

		statusClass(status) {
			if (['Em andamento', 'Carregando', 'Ag. Descarga'].includes(status)) {
				return 'is-active';
			}
			if (status === 'Finalizada') {
				return 'is-done';
			}
			if (status === 'Cancelada') {
				return 'is-cancelled';
			}
			return 'is-pending';
		},

		statusIcon(status) {
			if (['Em andamento', 'Carregando', 'Ag. Descarga'].includes(status)) {
				return 'mdi-truck-fast-outline';
			}

			if (status === 'Finalizada') {
				return 'mdi-check-decagram-outline';
			}

			if (status === 'Cancelada') {
				return 'mdi-close-circle-outline';
			}

			return 'mdi-timer-sand';
		},

		getParadaTimelineColor(tipo) {
			const normalized = String(tipo || '').toLowerCase();

			if (normalized.includes('coleta') || normalized.includes('origem') || normalized.includes('carrega')) {
				return '#10b981';
			}

			if (normalized.includes('entrega') || normalized.includes('destino') || normalized.includes('descarga')) {
				return '#2563eb';
			}

			return '#94a3b8';
		},

		getParadaTimelineIcon(tipo) {
			const normalized = String(tipo || '').toLowerCase();

			if (normalized.includes('coleta') || normalized.includes('origem') || normalized.includes('carrega')) {
				return 'mdi-truck-delivery-outline';
			}

			if (normalized.includes('entrega') || normalized.includes('destino') || normalized.includes('descarga')) {
				return 'mdi-flag-checkered';
			}

			return 'mdi-map-marker-outline';
		},

		abrirDialogDespesas(item) {
			if (!item?.id) {
				return;
			}

			this.viagemDespesa = {
				id: item.id,
				codigo: item.codigo,
				despesa: item.despesa,
			};
			this.despesaDialogOpen = true;
		},

		handleDespesaDialog(newValue) {
			this.despesaDialogOpen = newValue;

			if (!newValue) {
				this.viagemDespesa = {};
			}
		},

		async handleDespesaSalva(payload) {
			if (!payload?.viagemId) {
				await this.fetchViagens();
				return;
			}

			if (this.viagemDetalhes && String(this.viagemDetalhes.id) === String(payload.viagemId)) {
				try {
					this.viagemDetalhes = await viagensServices.buscarPorId(payload.viagemId);
				} catch (error) {
					// Mantem os detalhes atuais se a recarga falhar.
				}
			}

			await this.fetchViagens();
		},

		novaViagem() {
			this.cadastroDialogOpen = true;
		},

		abrirDialogContratantes() {
			this.contratantesDialogOpen = true;
			this.fetchContratantes();
		},

		onContratanteCnpjInput() {
			this.contratanteForm.cnpj = this.formatCnpj(this.contratanteForm.cnpj);
		},

		abrirDialogCadastroContratante() {
			this.contratanteFormDialogOpen = true;
			this.contratanteForm = {
				id: null,
				nome: '',
				cnpj: '',
			};
			this.contratanteFormValid = false;
			this.$nextTick(() => {
				this.$refs?.contratanteFormRef?.resetValidation?.();
			});
		},

		abrirDialogEditarContratante(item) {
			if (!item?.id) {
				return;
			}

			this.contratanteSelecionado = { ...item };
			this.contratanteFormDialogOpen = true;
			this.contratanteForm = {
				id: item.id,
				nome: item.nome || '',
				cnpj: this.formatCnpj(item.cnpj),
			};
			this.contratanteFormValid = false;
			this.$nextTick(() => {
				this.$refs?.contratanteFormRef?.resetValidation?.();
			});
		},

		abrirDialogExcluirContratante(item) {
			if (!item?.id) {
				return;
			}

			this.contratanteSelecionado = { ...item };
			this.contratanteDeleteDialogOpen = true;
		},

		async salvarContratante() {
			const isValid = this.$refs?.contratanteFormRef?.validate?.();
			if (!isValid) {
				return;
			}

			this.savingContratante = true;
			try {
				const payload = {
					nome: this.contratanteForm.nome,
					cnpj: this.parseCnpj(this.contratanteForm.cnpj),
				};

				const response = this.isEditingContratante
					? await contratantesServices.atualizar(this.contratanteForm.id, payload)
					: await contratantesServices.criar(payload);

				const contratanteSalvo = response?.data
					? {
						id: response.data.id,
						nome: response.data.nome,
						cnpj: response.data.cnpj || null,
						label: this.buildContratanteLabel(response.data),
					}
					: null;

				if (contratanteSalvo) {
					this.contratantes = [
						...this.contratantes.filter((item) => String(item.id) !== String(contratanteSalvo.id)),
						contratanteSalvo,
					].sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR'));
				}

				this.$toasted?.show(this.isEditingContratante ? 'Contratante atualizado com sucesso' : 'Contratante cadastrado com sucesso');
				this.contratanteFormDialogOpen = false;
				this.contratanteSelecionado = {};
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || (this.isEditingContratante ? 'Erro ao atualizar contratante' : 'Erro ao cadastrar contratante'));
			} finally {
				this.savingContratante = false;
			}
		},

		async confirmarExclusaoContratante() {
			if (!this.contratanteSelecionado?.id) {
				return;
			}

			this.deletingContratante = true;
			try {
				await contratantesServices.excluir(this.contratanteSelecionado.id);
				this.contratantes = this.contratantes.filter((item) => String(item.id) !== String(this.contratanteSelecionado.id));
				this.contratanteDeleteDialogOpen = false;
				this.contratanteSelecionado = {};
				this.$toasted?.show('Contratante excluido com sucesso');
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao excluir contratante');
			} finally {
				this.deletingContratante = false;
			}
		},

		handleCadastroSalvo() {
			this.cadastroDialogOpen = false;
			this.fetchViagens();
		},

		handleEdicaoDialog(newValue) {
			this.editarDialogOpen = newValue;

			if (!newValue) {
				this.viagemEmEdicao = null;
			}
		},

		handleEdicaoSalva(viagem) {
			this.editarDialogOpen = false;
			this.viagemEmEdicao = null;

			if (this.viagemDetalhes && this.viagemDetalhes.id === viagem?.id) {
				this.viagemDetalhes = viagem;
			}

			this.fetchViagens();
		},

		async editarViagem(item) {
			if (!item?.id) {
				return;
			}

			try {
				this.viagemEmEdicao = await viagensServices.buscarPorId(item.id);
				this.editarDialogOpen = true;
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Nao foi possivel carregar a viagem para edicao');
			}
		},

		abrirDialogStatus(item) {
			if (!item?.id) {
				return;
			}

			this.viagemStatusEdicao = {
				id: item.id,
				status: item.status || 'Pendente',
			};
			this.statusDialogOpen = true;
		},

		async salvarStatusViagem() {
			if (!this.viagemStatusEdicao?.id || !this.viagemStatusEdicao?.status) {
				return;
			}

			this.statusSaving = true;
			try {
				const response = await viagensServices.atualizarStatus(
					this.viagemStatusEdicao.id,
					this.viagemStatusEdicao.status,
				);

				this.$toasted?.show('Status da viagem atualizado com sucesso.');
				this.statusDialogOpen = false;

				if (this.viagemDetalhes && this.viagemDetalhes.id === response?.data?.id) {
					this.viagemDetalhes = response.data;
				}

				await this.fetchViagens();
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao atualizar status da viagem.');
			} finally {
				this.statusSaving = false;
			}
		},

		abrirDialogExclusao(item) {
			if (!item?.id) {
				return;
			}

			this.viagemExclusao = { ...item };
			this.exclusaoDialogOpen = true;
		},

		async confirmarExclusaoViagem() {
			if (!this.viagemExclusao?.id) {
				return;
			}

			this.exclusaoSaving = true;
			try {
				await viagensServices.excluir(this.viagemExclusao.id);
				this.$toasted?.show('Viagem excluida com sucesso.');
				this.exclusaoDialogOpen = false;

				if (this.viagemDetalhes && this.viagemDetalhes.id === this.viagemExclusao.id) {
					this.fecharDetalhes();
				}

				this.viagemExclusao = {};
				await this.fetchViagens();
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao excluir viagem.');
			} finally {
				this.exclusaoSaving = false;
			}
		},

		async verDetalhes(item) {
			if (!item?.id) {
				return;
			}

			this.detalhesDialogOpen = true;
			this.detalhesLoading = true;
			this.viagemDetalhes = null;

			try {
				this.viagemDetalhes = await viagensServices.buscarPorId(item.id);
			} catch (error) {
				this.detalhesDialogOpen = false;
				this.$toasted?.show(error?.response?.data?.error || 'Nao foi possivel carregar os detalhes da viagem');
			} finally {
				this.detalhesLoading = false;
			}
		},

		fecharDetalhes() {
			this.detalhesDialogOpen = false;
			this.viagemDetalhes = null;
		},
	},

	mounted() {
		this.loadVisibleColumnsPreference();
		this.fetchViagens();
		this.fetchContratantes();
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.viagem-page {
	font-family: "Inter", sans-serif;
	max-width: 1250px;
}

.viagem-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 16px;
	flex-wrap: wrap;
}

.viagem-header > div:first-child {
	flex: 1;
}

.viagem-header__actions {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.viagem-icon-btn {
	width: 38px;
	height: 38px;
	min-width: 38px !important;
	border-radius: 12px !important;
	background-color: #ffffff !important;
	color: #10b981 !important;
	border: 2px solid #10b981 !important;
}

.viagem-table-card__toolbar {
	display: flex;
	justify-content: flex-end;
	padding: 0 12px;
	margin-top: -20px;
}

.viagem-title {
	margin: 0;
	color: #10b981;
	font-size: 22px;
	font-weight: 800;
	line-height: 1.2;
}

.viagem-subtitle {
	color: #7a8ca6;
	font-size: 13px;
	margin: 6px 0 0;
	max-width: 760px;
}

.viagem-new-btn {
	background-color: #10b981 !important;
	color: #ffffff !important;
	border-radius: 12px;
	font-weight: 700;
	letter-spacing: 0.01em;
	padding: 10px 16px;
	text-transform: none;
}

.viagem-secondary-btn {
	background-color: #ffffff !important;
	color: #10b981 !important;
	border: 2px solid #10b981 !important;
	border-radius: 12px;
	font-weight: 700;
	letter-spacing: 0.01em;
	padding: 10px 16px;
	text-transform: none;
}

.viagem-colunas-card {
	min-width: 280px;
	border-radius: 16px !important;
	border-color: #e6ebf1 !important;
	box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08) !important;
}

.viagem-colunas-card__title {
	color: #1f2a44;
	font-size: 14px;
	font-weight: 800;
	padding: 12px 16px;
}

.viagem-colunas-card__hint {
	color: #7a8ca6;
	font-size: 12px;
	margin-bottom: 10px;
	line-height: 1.4;
}

.viagem-colunas-card__checkbox {
	margin-bottom: 4px;
}

.viagem-colunas-card__checkbox::v-deep .v-label {
	color: #1f2a44;
	font-size: 13px;
}

.viagem-stat {
	border-radius: 16px !important;
	border: 1px solid #e7edf5 !important;
	min-height: 106px;
	background: #ffffff;
}

.viagem-stat__content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px;
}

.viagem-stat__label {
	color: #7a8497;
	font-size: 13px;
	margin-bottom: 4px;
}

.viagem-stat__value {
	color: #1f2a44;
	font-size: 26px;
	font-weight: 700;
}

.viagem-stat__icon {
	width: 40px;
	height: 40px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.viagem-stat__icon--primary {
	background: #eff6ff;
}

.viagem-stat__icon--accent {
	background: #ecfdf5;
}

.viagem-stat__icon--done {
	background: #dbeafe;
}

.viagem-table-card {
	border-radius: 16px;
	border-color: #e6ebf1 !important;
}

.viagem-table {
	border-radius: 16px;
}

.viagem-table ::v-deep th {
	color: #7a8ca6 !important;
	font-weight: 800 !important;
	font-size: 11px !important;
	letter-spacing: 0.08em;
}

.viagem-table ::v-deep td {
	color: #1f2a44;
	font-size: 13px !important;
}

.viagem-table ::v-deep .v-data-table__wrapper {
	border-radius: 16px;
}

.viagem-status {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 999px;
	padding: 4px 10px;
	font-size: 12px;
	font-weight: 800;
}

.viagem-confirmacao__texto {
	color: #1f2a44;
	font-size: 14px;
	line-height: 1.5;
}

.viagem-confirmacao__aviso {
	margin-top: 10px;
	color: #7a8ca6;
	font-size: 13px;
}

.viagem-status.is-active {
	background: #ecfdf5;
	color: #10b981;
}

.viagem-status.is-done {
	background: #eff6ff;
	color: #2563eb;
}

.viagem-status.is-cancelled {
	background: #fef2f2;
	color: #ef4444;
}

.viagem-status.is-pending {
	background: #fefce8;
	color: #ca8a04;
}

.viagem-rota,
.viagem-veiculo {
	display: inline-flex;
	align-items: center;
}

.viagem-despesa-cell {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	white-space: nowrap;
}

.viagem-despesa-cell__action {
	background: #f3faf7;
	border-radius: 8px;
}

.viagem-rota__origem {
	font-weight: 600;
}

.viagem-actions {
	background: #f3faf7;
	border-radius: 10px;
}

.viagem-actions__menu {
	min-width: 180px;
	border: 1px solid #e6edf6;
	border-radius: 12px;
	padding: 4px 0;
}

.contratante-table-actions {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.viagem-delete-btn {
	background-color: #ef4444 !important;
	color: #ffffff !important;
	border-radius: 12px;
	text-transform: none;
	font-weight: 700;
	letter-spacing: 0.01em;
	padding: 10px 16px;
}

.viagem-dialog {
	border-radius: 16px !important;
	border-color: #e6ebf1 !important;
}

.viagem-dialog__title {
	color: #1f2a44;
	font-weight: 800;
}

.viagem-section-title {
	color: #10b981;
	font-size: 16px;
	font-weight: 700;
}

.parada-box {
	border: 1px solid #e6ebf1;
	border-radius: 16px;
	padding: 16px;
	background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
	box-shadow: 0 14px 28px rgba(15, 23, 42, 0.06);
}

.viagem-paradas-timeline {
	padding-top: 4px;
}

.viagem-paradas-timeline::v-deep .v-timeline-item__body {
	max-width: none;
	padding-bottom: 10px;
}

.viagem-paradas-timeline::v-deep .v-timeline-item__divider {
	min-width: 56px;
}

.viagem-paradas-timeline::v-deep .v-timeline-item__dot {
	box-shadow: 0 10px 18px rgba(15, 23, 42, 0.12);
}

.parada-box__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	margin-bottom: 14px;
	color: #1f2a44;
	gap: 12px;
}

.parada-box__identity {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.parada-box__eyebrow {
	color: #10b981;
	font-size: 11px;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
}

.parada-box__subtitle {
	color: #7a8ca6;
	font-size: 13px;
	line-height: 1.4;
}

.parada-box__tipo {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 6px 10px;
	border-radius: 999px;
	background: #eff6ff;
	border: 1px solid #dbeafe;
	color: #2563eb;
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.parada-box__details {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 12px;
}

.parada-box__detail-card {
	padding: 12px;
	border-radius: 12px;
	background: #f8fafc;
	border: 1px solid #e6edf6;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.parada-box__detail-card strong {
	color: #1f2a44;
	font-size: 14px;
	line-height: 1.4;
}

.parada-box__detail-header {
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.parada-box__detail-label {
	color: #7a8ca6;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
}

.parada-box__note {
	margin-top: 12px;
	padding: 12px;
	border-radius: 12px;
	background: #fbfdff;
	border: 1px dashed #d7e3ef;
}

.parada-box__note p {
	margin: 8px 0 0;
	color: #1f2a44;
	font-size: 13px;
	line-height: 1.6;
}

.viagem-detalhes__loading {
	display: flex;
	justify-content: center;
	padding: 28px 0;
}

.viagem-detalhes__empty {
	padding: 18px;
	border: 1px dashed #d8e2ee;
	border-radius: 12px;
	color: #7a8ca6;
	background: #fbfdff;
}

.viagem-detalhes__parada-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 8px 16px;
	color: #1f2a44;
	font-size: 13px;
}

.viagem-detalhes__full {
	grid-column: 1 / -1;
}

.is-done {
	color: #2563eb;
}

@media (max-width: 960px) {
	.viagem-page {
		padding: 24px 16px !important;
	}

	.viagem-title {
		font-size: 24px;
	}

	.viagem-subtitle {
		font-size: 14px;
	}

	.viagem-new-btn {
		width: 100%;
	}

	.viagem-icon-btn {
		width: 38px;
		height: 38px;
		min-width: 38px !important;
	}

	.viagem-secondary-btn {
		width: 100%;
	}

	.viagem-detalhes__parada-grid {
		grid-template-columns: 1fr;
	}

	.parada-box__header {
		flex-direction: column;
		align-items: flex-start;
	}

	.parada-box__details {
		grid-template-columns: 1fr;
	}

	.viagem-paradas-timeline::v-deep .v-timeline-item__divider {
		min-width: 44px;
	}
}
</style>
